"use client";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider, usePostHog } from "posthog-js/react";
import { Suspense, useEffect, useContext } from "react";
import { useLocation } from "@tanstack/react-router";
import { AuthContext } from "./AuthContextProvider"; 

// Track if PostHog has been initialized to prevent duplicate initialization
let posthogInitialized = false;
let errorHandlersInstalled = false;

// Check if we're in development mode
const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development';

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  // Use useContext directly to avoid throwing error if AuthProvider isn't ready
  const auth = useContext(AuthContext);
  const { user, isAuthenticated, anonId } = auth || {}; 

  useEffect(() => {
    // Don't initialize PostHog in development
    if (isDevelopment) {
      console.log('[PostHog] Disabled in development mode');
      return;
    }

    // Fetch PostHog config from API endpoint (keeps API key server-side)
    const initPostHog = async () => {
      try {
        const response = await fetch('/api/public-config');
        const config = await response.json();
        
        if (config.posthog?.enabled && !posthogInitialized) {
          posthog.init(config.posthog.key, {
            api_host: "/api/ph",
            ui_host: "https://eu.posthog.com",
            capture_pageview: false, // We capture pageviews manually
            capture_pageleave: true, // Enable pageleave capture
            capture_performance: true,
            debug: false, // Disable debug logs in production
          });
          posthogInitialized = true;
          console.log('[PostHog] Initialized successfully');
        } else if (!config.posthog?.enabled) {
          console.log('[PostHog] Disabled - no API key configured');
        }
      } catch (error) {
        console.error('[PostHog] Failed to fetch config:', error);
      }
    };
    
    initPostHog();
  }, []);

  // Install global error handlers
  useEffect(() => {
    if (errorHandlersInstalled || isDevelopment) return;

    // Handle uncaught errors
    const handleError = (event: ErrorEvent) => {
      console.error('Uncaught error:', event.error);
      
      try {
        posthog.capture('$exception', {
          $exception_type: event.error?.name || 'Error',
          $exception_message: event.error?.message || event.message,
          $exception_stack_trace: event.error?.stack,
          $exception_source: 'window.onerror',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          timestamp: new Date().toISOString(),
        });
      } catch (posthogError) {
        console.error('Failed to capture error in PostHog:', posthogError);
      }
    };

    // Handle unhandled promise rejections
    const handleRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      
      try {
        const error = event.reason;
        posthog.capture('$exception', {
          $exception_type: error?.name || 'UnhandledRejection',
          $exception_message: error?.message || String(event.reason),
          $exception_stack_trace: error?.stack,
          $exception_source: 'unhandledrejection',
          timestamp: new Date().toISOString(),
        });
      } catch (posthogError) {
        console.error('Failed to capture rejection in PostHog:', posthogError);
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    errorHandlersInstalled = true;

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
      errorHandlersInstalled = false;
    };
  }, []);

  useEffect(() => {
    // Don't identify users in development
    if (isDevelopment) return;

    // Only identify with anonId if it exists and is stable
    if (!isAuthenticated && anonId) {
      posthog.identify(anonId);
    } else if (isAuthenticated && user?.id) {
      posthog.identify(user.id, { email: user.email || undefined });
    }
  }, [isAuthenticated, user, anonId]);

  useEffect(() => {
    // Don't track auth changes in development
    if (isDevelopment) return;

    let prevAuth = isAuthenticated;
    // Track authentication changes for analytics
    return () => {
      if (!prevAuth && isAuthenticated && user?.id && anonId) {
        // Anonymous -> Authenticated: alias anonId to userId
        if (typeof posthog !== 'undefined' && posthog.alias) {
          posthog.alias(anonId, user.id);
        }
      }
      prevAuth = isAuthenticated;
    };
  }, [isAuthenticated, user, anonId]);

  return (
    <PHProvider client={posthog}>
      <SuspendedPostHogPageView />
      {children}
    </PHProvider>
  );
}

function PostHogPageView() {
  const location = useLocation();
  const pathname = location.pathname;
  const searchParams = location.searchStr;
  const posthogInstance = usePostHog();

  useEffect(() => {
    // Don't capture pageviews in development
    if (isDevelopment) return;

    if (pathname && posthogInstance) {
      let url = window.origin + pathname;
      if (searchParams) {
        url += searchParams;
      }
      posthogInstance.capture("$pageview", { $current_url: url });
    }
  }, [pathname, searchParams, posthogInstance]);

  return null;
}

function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  );
}
